<template>
	<FormCard class="">
		<h3>
			{{
				"Life Cycle Breakdown" +
				(props.tabName === tabsDefinitions.packaging.value
					? ` - per ${props.calculatedQuantity} ${props.calculatedFormatLabel}`
					: " - per kg Drug Product")
			}}
		</h3>
		<div class="flex">
			<ag-grid-vue
				:theme="resultGridTheme"
				style="width: 100%; min-height: 350px"
				:rowData="filteredResultByLifeCycleStage"
				:columnDefs="agGridColumnDefs"
				:loadingOverlayComponentParams="{ loadingMessage: 'Loading result by step. Please wait.' }"
				:noRowsOverlayComponentParams="{ noRowsMessageFunc: () => 'No result by step found.' }"
				:overlayLoadingTemplate="'<span class=&quot;ag-overlay-loading-center&quot;>Loading result by step. Please wait.</span>'"
				:overlayNoRowsTemplate="'<span class=&quot;ag-overlay-no-rows-center&quot;>No result by step found.</span>'"
				:context="{}"
				:loadThemeGoogleFonts="true"
				:defaultColDef="{ wrapHeaderText: true, autoHeaderHeight: true }"
				:pinnedBottomRowData="pinnedBottomRow"
				@grid-ready="onGridReady"
			>
			</ag-grid-vue>
		</div>

		<!-- Chart Type Toggle -->
		<div class="flex mt-2 mb-2 justify-center">
			<div class="flex items-center space-x-4">
				<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPieChart, 'text-gray-500': showPieChart }">Bar Chart</span>
				<label class="relative inline-flex items-center cursor-pointer">
					<input type="checkbox" v-model="showPieChart" class="sr-only peer" />
					<div
						class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
					></div>
				</label>
				<span class="text-sm font-medium" :class="{ 'text-blue-600': showPieChart, 'text-gray-500': !showPieChart }">Pie Chart</span>
			</div>
		</div>
		<!-- Dynamic Charts Component with CSS Grid Layout -->
		<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
			<DynamicChartComponent
				v-for="emissionFactor in props.activeEmissionFactors"
				:key="emissionFactor.id"
				:title="props.isWeighted ? emissionFactor.emissionFactorName?.replace(/ \[.*\]$/, ' [mPt]') : emissionFactor.emissionFactorName"
				:emissionFactors="emissionFactor"
				:chartData="chartDataItems"
				:showPieChart="showPieChart"
				class="min-h-96 flex flex-col"
			/>
		</div>
	</FormCard>
</template>
<script lang="ts" setup>
import { ref, onMounted, computed } from "vue"
import { FinalResult, EmissionFactors } from "../../entities/NSwagGenerated"
import DynamicChartComponent from "./DynamicChartComponent.vue"
import type { ChartDataItem } from "./DynamicChartComponent.vue"
import { useTabsDefinitions } from "../composables/tabsDefinitions"
const tabsDefinitions = useTabsDefinitions()
const filteredResultByLifeCycleStage = computed(
	() =>
		props.finalResult.resultByLifeCycleStage?.filter((row) => typeof row.stageName !== "string" || row.stageName.trim().toLowerCase() !== "total") ??
		[]
)

// Computed property to map filteredResultByLifeCycleStage to ChartDataItem format
const chartDataItems = computed((): ChartDataItem[] => {
	return filteredResultByLifeCycleStage.value.map((item) => ({
		name: item.stageName || "",
		emissionFactors: item.emissionFactors || {},
	}))
})

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	tabName: {
		type: String,
		required: false,
		default: "",
	},
	finalResult: {
		type: FinalResult,
		required: false,
		default: {},
	},
	activeEmissionFactors: {
		type: Array<EmissionFactors>,
		required: false,
		default: [],
	},
	isWeighted: {
		type: Boolean,
		required: false,
		default: false,
	},
	referenceUnitName: {
		type: String,
		required: false,
		default: "",
	},
	calculateResultsBy: {
		type: String,
		required: false,
		default: "Reference Unit",
	},
	calculatedQuantity: {
		type: Number,
		required: false,
		default: 1,
	},
	calculatedFormatLabel: {
		type: String,
		required: false,
		default: "",
	},
})

import { GridApi, ColDef, CellStyle, GridReadyEvent, ValueGetterParams, ValueFormatterParams } from "ag-grid-community"
const gridApi = ref<GridApi | null>(null)

const pinnedBottomRow = computed(() => {
	if (!props.finalResult.resultByLifeCycleStage?.length) return []

	const totals = {
		stageName: "Grand Total",
		totalWeightedScore: 0,
		emissionFactors: {} as { [key: string]: number },
	}

	// Exclude "Total" row from sum to avoid double-counting
	for (const row of props.finalResult.resultByLifeCycleStage.filter((r) => r.stageName?.toLowerCase() !== "total")) {
		totals.totalWeightedScore += row.totalWeightedScore || 0
		if (row.emissionFactors) {
			for (const ef of props.activeEmissionFactors) {
				if (!ef.emissionFactorName) continue
				totals.emissionFactors[ef.emissionFactorName] =
					(totals.emissionFactors[ef.emissionFactorName] || 0) + (row.emissionFactors[ef.emissionFactorName] || 0)
			}
		}
	}

	return [totals]
})

function onGridReady(params: GridReadyEvent) {
	gridApi.value = params.api
}

import { AgGridVue } from "ag-grid-vue3"
import {
	getConditionalFormatColor,
	valueToStringFormatter,
	resultGridTheme,
	BuildMinMaxValuesFromEmissionFactors,
} from "../composables/resultGridHelper"

// Chart and dropdown state

const columnMinMax = computed(() => {
	if (!filteredResultByLifeCycleStage.value?.length) return []

	const emissionFactors = filteredResultByLifeCycleStage.value.map((item) => item.emissionFactors ?? {})
	return BuildMinMaxValuesFromEmissionFactors(emissionFactors)
})

const agGridColumnDefs = computed(() => [
	{ field: "stageName", headerName: "Life Cycle Stage" },
	...(props.isWeighted
		? [
				{
					field: "totalWeightedScore",
					headerName: "Total Weighted Score [mPt]",
					valueGetter: (p: ValueGetterParams) => {
						return p.data?.totalWeightedScore ?? null
					},
					valueFormatter: (p: any) => valueToStringFormatter(p.value, false),
					cellStyle: (): CellStyle => ({
						textAlign: "right",
						fontWeight: "bold",
					}),
					aggFunc: "sum",
				},
			]
		: []),
	...props.activeEmissionFactors.map<ColDef>((ef) => ({
		field: `emissionFactors.${ef.emissionFactorName}`,
		headerName: props.isWeighted ? ef.emissionFactorName?.replace(/ \[.*\]$/, " [mPt]") : ef.emissionFactorName,
		valueGetter: (p: any) => {
			const value = p.data?.emissionFactors?.[ef.emissionFactorName!] ?? null
			return value
		},
		valueFormatter: (p: any) => {
			// Use p.value from valueGetter instead of re-extracting from data
			return valueToStringFormatter(p.value, false)
		},
		cellStyle: (params: any): CellStyle | null => {
			// Don't apply conditional formatting to pinned rows
			if (params.node.rowPinned) {
				return { textAlign: "right", backgroundColor: "transparent" }
			}
			let minMax = columnMinMax.value.find((item) => item.columnName === ef.emissionFactorName)
			if (minMax) {
				return {
					backgroundColor: getConditionalFormatColor(params.value, minMax.min, minMax.max),
					textAlign: "right",
				}
			}
			return { "text-align": "right" }
		},
		aggFunc: "sum",
	})),
])

// Function to get grid API reference
const getGridApi = () => gridApi.value

// Function to get results data
const getResults = () => filteredResultByLifeCycleStage.value

const showPieChart = ref(true)

// Expose methods for parent components
defineExpose({
	getResults,
	getGridApi,
})

onMounted(() => {
	// Component mounted
})
</script>
